import React from 'react';
import { Info } from 'lucide-react';
import { InfoBadge } from '../UI/InfoBadge';
import { formatDate } from '../../utils/dateUtils';

const REASON_BADGE = {
  day_off_override: { color: 'gray', label: 'Day off' },
  public_holiday: { color: 'purple', label: 'Public holiday' },
};

const CALENDAR_DAY_TYPES = /home leave|urgent leave|claim:\s*off in lieu/i;

/** Template rest days (usually Sunday) are expected. Do not chip them. */
const SURPRISING_REASONS = new Set(['public_holiday', 'day_off_override']);

export function isWorkingDayLeaveType(name = '') {
  return !CALENDAR_DAY_TYPES.test(name || '');
}

export function surprisingExcludedDates(excludedDates = []) {
  return (excludedDates || []).filter((day) => SURPRISING_REASONS.has(day.reason_code));
}

/**
 * Names dates inside a leave range that people would otherwise assume count.
 *
 * Public holidays and rostered day-off overrides (alternate Saturday, project
 * day off) change the billed total. Ordinary rest days do not — everyone
 * already knows Sunday is off. Chip hues match Attendance Public Holiday /
 * Day Off (DS Attendance Table Color Scheme).
 */
export default function LeaveExcludedDaysCallout({
  excludedDates = [],
  leaveTypeName = '',
  audience = 'employee',
}) {
  if (!isWorkingDayLeaveType(leaveTypeName)) return null;

  const surprising = surprisingExcludedDates(excludedDates);
  if (!surprising.length) return null;

  const isAdmin = audience === 'admin';

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4" role="status">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-blue-900">
            {isAdmin
              ? 'These days are not charged to leave'
              : 'These days don\u2019t use your leave'}
          </h4>
          <p className="text-sm text-blue-800 mt-1">
            {isAdmin
              ? 'They stay in the date range, but a public holiday or a rostered day off already treats them as off.'
              : 'They stay in the date range, but a public holiday or a rostered day off already treats them as off.'}
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {surprising.map((day) => {
          const badge = REASON_BADGE[day.reason_code] || {
            color: 'gray',
            label: day.reason || 'Off',
          };
          return (
            <InfoBadge
              key={day.date}
              color={badge.color}
              size="xs"
              label={`${formatDate(day.date)} · ${badge.label}`}
              title={day.reason}
            />
          );
        })}
      </div>
    </div>
  );
}
